import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Stack from '@mui/material/Stack';
import FleetWidget, { WidgetField } from 'models/fleet-management/FleetWidget';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import TextInput from '@common/components/Inputs/TextInput';
import { BucketSize, FieldType, FilterType, FleetWidgetType } from 'models/enums/fleet-management';
import SelectInput from 'components/forms/SelectInput';
import { useAppSelector } from 'store';
import OptionSwitchInput from '@common/components/Inputs/OptionSwitchInput';
import Button from '@mui/material/Button';
import CustomWidgetIcon from './CustomWidgetIcon';
import {
  selectFleetDataSetsData,
  selectFleetFieldsConfiguration,
  selectFleetWidgetsConfiguration,
} from 'store/fleet-management/fleet-management-slice-selectors';
import { AddIcon, CopyIcon } from '@common/utils/icons/icons';
import FieldInput from './FieldInput';
import FilterInput from './FilterInput';
import { CUMULATIVE_OPERATIONS } from 'utils/fleet-filters';
import { FleetDataFilter } from 'models/fleet-management/FleetDataFilter';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';

const WidgetsForm: React.FC<{
  widgets: FleetWidget[];
  selectedId: string;
  onChange: (newWidgets: FleetWidget[]) => void;
}> = ({ widgets, selectedId, onChange }) => {
  const { t } = useTranslation();

  const fieldConfigs = useAppSelector((state) => selectFleetFieldsConfiguration(state));
  const datasets = useAppSelector((state) => selectFleetDataSetsData(state));
  const widgetConfigs = useAppSelector((state) => selectFleetWidgetsConfiguration(state));

  const [copyFiltersAnchorEl, setCopyFiltersAnchorEl] = useState<HTMLButtonElement | null>(null);

  const selectedWidget = widgets.find((w) => w.id === selectedId);
  const widgetConfig = widgetConfigs?.find((wC) => wC.type === selectedWidget?.type);
  const dataset = datasets?.find((ds) => ds.id === selectedWidget?.dataset);
  const fields = useMemo(
    () =>
      fieldConfigs != null
        ? fieldConfigs.filter(
            (f) => f.model === dataset?.model && dataset.fields?.some((df) => df === f.fieldName),
          )
        : null,
    [dataset?.fields, dataset?.model, fieldConfigs],
  );

  useEffect(() => {
    if ((selectedWidget == null || widgets.length < 2) && copyFiltersAnchorEl != null) {
      setCopyFiltersAnchorEl(null);
    }
  }, [copyFiltersAnchorEl, selectedWidget, widgets.length]);

  const updateWidgetHandler = (update: Partial<FleetWidget>) => {
    if (datasets == null || datasets?.length === 0) return;

    const currentWidget: FleetWidget = widgets.find((w) => w.id === (update.id ?? selectedId)) ?? {
      id: '',
      fields: [],
      name: '',
      type: FleetWidgetType.TEXT,
      grid: [
        [0, 0],
        [1, 1],
      ],
      dataset: dataset?.id ?? datasets[0].id,
      description: null,
      groups: null,
      filters: [],
      limit: null,
      orderBy: null,
      bucketField: null,
      bucket: null,
    };
    onChange([
      ...widgets.filter((w) => w.id !== currentWidget.id),
      { ...currentWidget, ...update },
    ]);
  };

  const datasetChangeHandler = (event: SelectChangeEvent<unknown>) => {
    updateWidgetHandler({
      dataset: event.target.value as string,
      fields: [],
      filters: [],
      groups: null,
      orderBy: null,
      bucketField: null,
      bucket: null,
    });
  };

  const nameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateWidgetHandler({ name: event.target.value });
  };

  const descriptionChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateWidgetHandler({ description: event.target.value });
  };

  const widgetTypeChangeHandler = (newValue: string) => {
    updateWidgetHandler({
      type: newValue as FleetWidgetType,
      fields: [],
      groups: null,
      bucketField: null,
      bucket: null,
    });
  };

  const fieldChangeHandler = (newValue: WidgetField, fieldIndex: number, toDelete?: boolean) => {
    const widgetFields = [...(selectedWidget?.fields ?? [])];
    if (toDelete) {
      widgetFields.splice(fieldIndex, 1);
    } else if (widgetFields.length <= fieldIndex) {
      widgetFields.push(newValue);
    } else {
      widgetFields[fieldIndex] = newValue;
    }
    updateWidgetHandler({ fields: widgetFields });
  };

  const addFieldClickHandler = () => {
    if (selectedWidget?.fields == null) return;
    updateWidgetHandler({ fields: [...selectedWidget.fields, { name: '', operation: null }] });
  };

  const groupChangeHandler = (newValue: string, groupIndex: number, toDelete?: boolean) => {
    const widgetGroups = [...(selectedWidget?.groups ?? [])];
    if (toDelete) {
      widgetGroups.splice(groupIndex, 1);
    } else if (widgetGroups.length <= groupIndex) {
      widgetGroups.push(newValue);
    } else {
      widgetGroups[groupIndex] = newValue;
    }
    updateWidgetHandler({ groups: widgetGroups });
  };

  const addGroupClickHandler = () => {
    if (selectedWidget?.groups == null) return;
    updateWidgetHandler({ groups: [...selectedWidget.groups, ''] });
  };

  const bucketFieldChangeHandler = (event: SelectChangeEvent<unknown>) => {
    updateWidgetHandler({ bucketField: event.target.value as string });
  };

  const bucketChangeHandler = (event: SelectChangeEvent<unknown>) => {
    updateWidgetHandler({ bucket: event.target.value as BucketSize });
  };

  const deleteWidgetClickHandler = () => {
    if (selectedWidget != null) {
      onChange([...widgets.filter((w) => w.id !== selectedWidget.id)]);
    }
  };

  const addFilterClickHandler = () => {
    if (selectedWidget?.filters == null) return;
    updateWidgetHandler({
      filters: [
        ...selectedWidget.filters,
        { field: '', id: uuidv4(), type: FilterType.EXACT, values: [] },
      ],
    });
  };

  const filterChangeHandler = (
    newValue: FleetDataFilter,
    filterIndex: number,
    toDelete?: boolean,
  ) => {
    const widgetFilters = [...(selectedWidget?.filters ?? [])];
    if (toDelete) {
      widgetFilters.splice(filterIndex, 1);
    } else if (widgetFilters.length <= filterIndex) {
      widgetFilters.push(newValue);
    } else {
      widgetFilters[filterIndex] = newValue;
    }
    updateWidgetHandler({ filters: widgetFilters });
  };

  const openCopyFiltersMenuClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setCopyFiltersAnchorEl(event.currentTarget);
  };

  const closeCopyFilterMenuHandler = () => {
    setCopyFiltersAnchorEl(null);
  };

  const copyWidgetsFiltersClickHandler = (filters: FleetDataFilter[]) => {
    updateWidgetHandler({ filters: filters.map((f) => ({ ...f, id: uuidv4() })) });
    setCopyFiltersAnchorEl(null);
  };

  if (selectedWidget == null) return null;

  return (
    <React.Fragment>
      <Stack spacing={6}>
        <Stack spacing={2}>
          <Stack>
            <InputLabel>{t('input-label-name')}</InputLabel>
            <TextInput size="small" value={selectedWidget.name} onChange={nameChangeHandler} />
          </Stack>

          <Stack>
            <InputLabel>{t('input-label-description')}</InputLabel>
            <TextInput
              size="small"
              value={selectedWidget.description ?? ''}
              onChange={descriptionChangeHandler}
            />
          </Stack>
        </Stack>

        <Stack spacing={4}>
          <Stack>
            <InputLabel>{t('input-label-widget-dataset')}</InputLabel>
            <SelectInput
              size="small"
              value={selectedWidget.dataset}
              onChange={datasetChangeHandler}
            >
              {(datasets ?? []).map((ds) => (
                <MenuItem key={ds.id} value={ds.id}>
                  {ds.name}
                </MenuItem>
              ))}
            </SelectInput>
          </Stack>

          <OptionSwitchInput
            options={Object.values(FleetWidgetType).map((i) => ({
              value: i,
              tooltip: t('label-widget-type-' + i),
              label: <CustomWidgetIcon icon={i} />,
            }))}
            value={selectedWidget.type}
            onChange={widgetTypeChangeHandler}
          />

          {widgetConfig?.fields != null && widgetConfig.fields !== 0 && fields != null && (
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <InputLabel>{t('input-label-widget-field-' + selectedWidget.type)}</InputLabel>
                {widgetConfig.fields === 2 && (
                  <Button size="small" startIcon={<AddIcon />} onClick={addFieldClickHandler}>
                    {t('button-add')}
                  </Button>
                )}
              </Stack>
              <Stack spacing={2}>
                {Array.from({ length: Math.max((selectedWidget.fields ?? []).length, 1) })
                  // Match slots to number of existing fields, the required for the widget, or 1 if infinite
                  .map((_, fieldIndex) => (
                    <FieldInput
                      key={fieldIndex}
                      fields={fields}
                      showOperation={selectedWidget.type !== FleetWidgetType.TABLE}
                      allowedOperations={
                        selectedWidget.type === FleetWidgetType.CUMULATIVE_CHART
                          ? CUMULATIVE_OPERATIONS
                          : undefined
                      }
                      index={fieldIndex}
                      operation={
                        selectedWidget.fields != null &&
                        selectedWidget.fields.length >= fieldIndex + 1
                          ? selectedWidget.fields[fieldIndex].operation
                          : null
                      }
                      value={
                        selectedWidget.fields != null &&
                        selectedWidget.fields.length >= fieldIndex + 1
                          ? selectedWidget.fields[fieldIndex].name
                          : ''
                      }
                      onFieldChange={fieldChangeHandler}
                    />
                  ))}
              </Stack>
            </Stack>
          )}

          {widgetConfig?.groups === true && fields != null && (
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <InputLabel>{t('input-label-widget-group-' + selectedWidget.type)}</InputLabel>
                <Button size="small" startIcon={<AddIcon />} onClick={addGroupClickHandler}>
                  {t('button-add')}
                </Button>
              </Stack>
              <Stack spacing={2}>
                {Array.from({ length: Math.max((selectedWidget.groups ?? []).length, 1) }).map(
                  (_, groupIndex) => (
                    <FieldInput
                      key={groupIndex}
                      fields={fields}
                      index={groupIndex}
                      value={
                        selectedWidget.groups != null &&
                        selectedWidget.groups.length >= groupIndex + 1
                          ? selectedWidget.groups[groupIndex]
                          : ''
                      }
                      onGroupChange={groupChangeHandler}
                    />
                  ),
                )}
              </Stack>
            </Stack>
          )}

          {widgetConfig?.bucket === true && fields != null && (
            <Stack spacing={2}>
              <Stack>
                <InputLabel>{t('input-label-widget-bucket-field')}</InputLabel>
                <SelectInput
                  size="small"
                  value={selectedWidget.bucketField ?? ''}
                  onChange={bucketFieldChangeHandler}
                >
                  {fields
                    .filter((f) => f.type === FieldType.DATE)
                    .map((f) => (
                      <MenuItem key={f.fieldName} value={f.fieldName}>
                        {t('fleet-management-field-' + f.fieldName)}
                      </MenuItem>
                    ))}
                </SelectInput>
              </Stack>
              <Stack>
                <InputLabel>{t('input-label-widget-bucket-size')}</InputLabel>
                <SelectInput
                  size="small"
                  value={selectedWidget.bucket ?? ''}
                  onChange={bucketChangeHandler}
                >
                  {Object.values(BucketSize).map((size) => (
                    <MenuItem key={size} value={size}>
                      {t('label-bucket-size-' + size)}
                    </MenuItem>
                  ))}
                </SelectInput>
              </Stack>
            </Stack>
          )}

          {widgetConfig?.filters === true && fields != null && (
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <InputLabel sx={{ flexGrow: 1 }}>{t('input-label-widget-filters')}</InputLabel>
                {widgets.length > 1 && (
                  <TooltipIconButton
                    icon={<CopyIcon />}
                    tooltip={t('button-copy-from-widget')}
                    onClick={openCopyFiltersMenuClickHandler}
                  />
                )}
                <Button size="small" startIcon={<AddIcon />} onClick={addFilterClickHandler}>
                  {t('button-add')}
                </Button>
              </Stack>
              <Stack spacing={2}>
                {Array.from({ length: Math.max((selectedWidget.filters ?? []).length, 1) }).map(
                  (_, filterIndex) => (
                    <FilterInput
                      key={filterIndex}
                      fields={fields}
                      filterIndex={filterIndex}
                      datasetId={selectedWidget.dataset}
                      value={
                        selectedWidget.filters != null &&
                        selectedWidget.filters.length >= filterIndex + 1
                          ? selectedWidget.filters[filterIndex]
                          : null
                      }
                      onFilterChange={filterChangeHandler}
                    />
                  ),
                )}
              </Stack>
            </Stack>
          )}

          <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
            <Button onClick={deleteWidgetClickHandler} color="error">
              {t('button-delete-widget')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <Menu
        MenuListProps={{
          'aria-labelledby': 'fade-button',
        }}
        anchorEl={copyFiltersAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={copyFiltersAnchorEl != null}
        onClose={closeCopyFilterMenuHandler}
      >
        <Box
          sx={(theme) => ({
            width: theme.spacing(32),
            maxHeight: theme.spacing(24),
          })}
        >
          {widgets
            .filter((w) => w.id !== selectedWidget.id)
            .map((w) => (
              <MenuItem
                key={w.id}
                value={w.id}
                onClick={() => copyWidgetsFiltersClickHandler(w.filters)}
              >
                <Stack direction="row" justifyContent="space-between" width="100%">
                  <Typography>{w.name}</Typography>
                  <Typography>({w.filters.length})</Typography>
                </Stack>
              </MenuItem>
            ))}
        </Box>
      </Menu>
    </React.Fragment>
  );
};

export default WidgetsForm;
